import React, { useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { NavigationLockout } from '@confluence/navigation-lockout';
import type { RouteMatch } from '@confluence/route';

import type { WhiteboardNavigationService } from './services/whiteboard-navigation-service';
import { useWhiteboardNavigationLockout } from './useWhiteboardNavigationLockout';

export const i18n = defineMessages({
	defaultLockoutMessage: {
		id: 'whiteboard-common.navigation-lockout.default-lockout-message',
		description:
			'Default redirection warning message warning the user that navigating away from the current whiteboard will stop async tasks running in the background',
		defaultMessage:
			'An action hasn’t been completed on your whiteboard, leaving this tab will stop the action',
	},
});

export type WhiteboardLockoutState =
	| {
			isLockoutSet: true;
			message?: string;
	  }
	| {
			isLockoutSet: false;
	  };

export type WhiteboardLockoutProps = {
	navigationService: WhiteboardNavigationService;
	contentId: string;
};

/**
 * A component to setup and trigger the <NavigationLockout /> component
 * based on the lockout state retrieved from the useWhiteboardNavigationLockout hook
 */
export const WhiteboardNavigationLockout: React.FC<WhiteboardLockoutProps> = ({
	navigationService,
	contentId,
}: WhiteboardLockoutProps) => {
	const intl = useIntl();
	const lockoutState = useWhiteboardNavigationLockout(navigationService);

	const isSameRoute = useCallback(
		(currentRoute: RouteMatch, nextRoute: RouteMatch) => {
			const contentIdMatch =
				'contentId' in nextRoute.params && nextRoute.params['contentId'] === contentId;
			const routeNameMatch = currentRoute.name === nextRoute.name;

			return contentIdMatch && routeNameMatch;
		},
		[contentId],
	);

	return lockoutState.isLockoutSet ? (
		<NavigationLockout
			message={
				lockoutState.message ? lockoutState.message : intl.formatMessage(i18n.defaultLockoutMessage)
			}
			isSafeTransition={isSameRoute}
		/>
	) : null;
};
